import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { toast } from 'react-hot-toast'
import employeeAllowanceService from '../../../services/employeeAllowanceService'

const EditEmployeeAllowanceModal = ({ isOpen, onClose, allowance, onSuccess }) => {
  const [formData, setFormData] = useState({
    houseAllowance: '',
    transportAllowance: '',
    foodAllowance: '',
    healthAllowance: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Populate form when modal opens or allowance changes
  useEffect(() => {
    if (isOpen && allowance) {
      setFormData({
        houseAllowance: allowance.houseAllowance?.toString() || '',
        transportAllowance: allowance.transportAllowance?.toString() || '',
        foodAllowance: allowance.foodAllowance?.toString() || '',
        healthAllowance: allowance.healthAllowance?.toString() || ''
      })
    }
  }, [isOpen, allowance])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const validateForm = () => {
    const allowanceFields = ['houseAllowance', 'transportAllowance', 'foodAllowance', 'healthAllowance']
    for (const field of allowanceFields) {
      if (!formData[field] || parseFloat(formData[field]) < 0) {
        toast.error(`Please enter a valid ${field.replace('Allowance', ' allowance')}`)
        return false
      }
    }
    
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) return
    
    setIsSubmitting(true)
    
    try {
      const submitData = {
        houseAllowance: parseFloat(formData.houseAllowance),
        transportAllowance: parseFloat(formData.transportAllowance),
        foodAllowance: parseFloat(formData.foodAllowance),
        healthAllowance: parseFloat(formData.healthAllowance)
      }
      
      const response = await employeeAllowanceService.updateEmployeeAllowance(allowance._id, submitData)
      
      if (response.success) {
        toast.success(response.message || 'Employee allowance updated successfully')
        onSuccess()
        onClose()
      } else {
        toast.error(response.message || 'Failed to update employee allowance')
      }
    } catch (error) {
      console.error('Error updating employee allowance:', error)
      toast.error('Failed to update employee allowance')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen || !allowance) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-gray-200 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Edit Allowances</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Employee Info - Read Only */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee *
              </label>
              <input
                type="text"
                value={allowance.employee?.fullName || 'N/A'}
                readOnly
                className="w-full p-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Allowance Fields - Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* House Allowance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  House Allowance *
                </label>
                <input
                  type="number"
                  name="houseAllowance"
                  value={formData.houseAllowance}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              {/* Transport Allowance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transport Allowance *
                </label>
                <input
                  type="number"
                  name="transportAllowance"
                  value={formData.transportAllowance}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              {/* Food Allowance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Food Allowance *
                </label>
                <input
                  type="number"
                  name="foodAllowance"
                  value={formData.foodAllowance}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              {/* Health Allowance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Health Allowance *
                </label>
                <input
                  type="number"
                  name="healthAllowance"
                  value={formData.healthAllowance}
                  onChange={handleInputChange}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>
            </div>
          </form>
        </div>

        {/* Footer - Fixed */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditEmployeeAllowanceModal
